import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Field, Formik, FormikProps } from 'formik';
import { Button, Grid, styled} from '@mui/material';
import { useSelector } from 'react-redux';
import { isBoolean } from 'lodash';
import { ContactManagerLayout } from '@modules/ContactManager/components/Tabs/shared/ContactManagerLayout';
import { getOptions, queryParams } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { SaveButton } from '@shared/components/SaveButton';
import { AssetsFilter } from '@styles/ContactManager/TabsStyles';
import { AssetLayout, Item } from '@styles/ContactManager/Assets';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { FormWithPrompt } from '@shared/components/FormWithPrompt';
import { useQueryState } from '@shared/hooks/useQueryState';
import { getAssetDraftDataById, removeAssetDraftData, setAssetDraftData } from '@store/reducers/draftSlice';
import { useAppDispatch } from '@store/hooks';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { setIsDealStructureChanged } from '@store/reducers/dealSlice';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { ContactErrorMessage } from '@modules/ContactManager/components/constants';
import { RadioButtonListField } from '@shared/fields/RadioButtonListField';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { GridItem } from '@shared/styledComponents';
import { FormFieldLabel } from '@styles/Shared/StyledFormFields';
import { UnableToSaveModal } from '../../shared/UnableToSaveModal';
import { NContact } from '../Contact/validation';
import { InsuranceForm } from './components/InsuranceForm';
import { LienForm } from './components/LienForm';
import { RegistrationForm } from './components/RegistraionForm';
import { ValuationForm } from './components/ValuationForm';
import {
  AssetsProps,
  CustomGridItemProps,
  IAsset,
  ICoApplicant,
  IFormAsset,
  ILienHolder,
} from './interfaces';
import { requestCreateAsset, requestUpdateAsset } from './requests';
import { assetValidationSchema } from './validation';
import { getAssetFormModel, getAssetOptions, getAssetOwnerValue, getAssetRequestFormModel, setAssetOwnerValue } from './utils';
import { AssetForm } from './components/AssetForm';
import { createAssetValidationOptions, ValidationForm } from './components/ValidationForm';
import { TitleForm } from './components/TitleForm';

export const PULL_STATUS = 'pull';

export const AssetOwnerFormFieldLabel = styled(FormFieldLabel)`
  line-height: 2;
  font-size: 11px;
`;

export const AssetOwnerForm = () => {
  const formConfig: IFormFieldConfig[] = [
    {
      width: '100%',
      name: 'isAssetOwner',
      label: 'Are you the original owner of this vehicle?',
      type: 'label',
    },
    {
      name: 'isAssetOwner',
      label: 'Are you the original owner of this vehicle?',
      options: createAssetValidationOptions('isAssetOwner'),
      component: RadioButtonListField,
    },
  ];

  return (
      <>
        {formConfig.map(({ name, label, component, ...rest }, index) => (
          <GridItem
            key={index}
            
            style={{ paddingTop: 0 }}
          >
            {rest?.type === 'label' ? (
              <AssetOwnerFormFieldLabel>{label}</AssetOwnerFormFieldLabel>
            ) : (
              <Field
                {...rest}
                name={name}
                label={label}
                component={component}
                sx={{ m: 0, px: 1 }}
              />
            )}
          </GridItem>
        ))}
      </>
    );
};

const CustomGridItem = ({ children }: CustomGridItemProps) => (
  <Grid
    item
    xs={12}
    sm={12}
    md={6}
    lg={3}
  >
    {children}
  </Grid>
);

export const Assets = ({
  assets,
  reloadAssets,
  validatingAssets,
  deals,
  coApplicants,
  reloadHeaderData,
  onSave,
}: AssetsProps) => {
  const dispatch = useAppDispatch();
  const [contactIdParam] = useQueryState('id');
  const [assetIdParam, setAssetIdParam] = useQueryState('assetId');
  const draftData = useSelector(getAssetDraftDataById(contactIdParam));
  const dealId = queryParams()?.dealId;
  const [isFirstLoad, setFirstLoadStatus] = useState(true);
  const [autoPullValuation, setAutoPullValuation] = useState(false);
  const [autoPullValuationAfterSave, setAutoPullValuationAfterSave] = useState(false);

  const formikRef = useRef<FormikProps<IAsset> | null>(null);
  const {
    data: lienHolders,
    isLoading: isLienHoldersLoading,
    mutate: refreshLienHolders,
  } = useSWR<ILienHolder[]>(`${SERVICES.ASSET}/v2/lienholder`);

  const { 
    data: contact,
    isLoading: isContactLoading
  } = useSWR<NContact.Entity>(
    contactIdParam && `/${SERVICES.CONTACT}/contacts/${contactIdParam}`
  );

  const pickedDeal = useMemo(
    () => {
      if (deals?.length && assetIdParam) {
        const deal = deals.find((deal) => deal.isPicked && deal.assetId === assetIdParam);

        return deal;
      }
    }, 
    [deals, assetIdParam]
  );

  const zip = useMemo(
    () => contact?.addresses?.find((address: NContact.Address) => address.isCurrent)?.zip,
    [contact?.addresses]
  );

  const state = useMemo(
    () => contact?.addresses?.find((address: NContact.Address) => address.isCurrent)?.state,
    [contact?.addresses]
  );

  const isLoading = useMemo(
    () => isLienHoldersLoading || validatingAssets || isContactLoading,
    [isLienHoldersLoading, validatingAssets, isContactLoading]
  );
  
  const assetOptions = useMemo(() => getAssetOptions(assets), [assets]);

  const coApplicantOptions = useMemo(() => {
    const updatedcoApplicantOptions = coApplicants?.length
      ? coApplicants
          .filter((coApplicant: ICoApplicant) => !coApplicant.isDroppedContact)
          .map((coApplicant: ICoApplicant) => ({
            label: `${coApplicant.firstName} ${coApplicant.lastName}`,
            value: coApplicant.id,
          }))
      : [];
    return updatedcoApplicantOptions;
  }, [coApplicants]);

  const initialValues = useMemo(() => {
    if (!isLoading) {
      let selectedAsset;

        if (assetIdParam) {
          selectedAsset = assets?.find(asset => asset.id === assetIdParam);
        } else if (!assetIdParam && isFirstLoad && assets?.[0]) {
          selectedAsset = assets[0];
          setAssetIdParam(assets[0].id);

          setFirstLoadStatus(false);
        }

        if (isFirstLoad) {
          setFirstLoadStatus(false);
        }

        const assetFormValues = getAssetFormModel({ ...(selectedAsset as IAsset),  vin: selectedAsset?.vin?.toUpperCase() || ''}, contact);

      setTimeout(() => {
        formikRef.current?.resetForm();
      });
      return assetFormValues;
    }
  }, [assets, assetIdParam, contact, isLoading, isFirstLoad]);

  useEffect(
    () => {
      if (draftData && draftData.id === assetIdParam) {
        formikRef.current?.setValues(draftData, true);
      }
    },
    [draftData, assetIdParam]
  );

  useEffect(() => {
    // related to auto pull valuation after user click on pull button in Asset section
    // this executes after submit (new asset creation) to use newly created AssetId
    if (autoPullValuationAfterSave && assetIdParam != '') {
      setAutoPullValuation(true);
    }
  }, [assetIdParam, autoPullValuationAfterSave]);

  const handleSubmit = useCallback(
    async (data: IFormAsset) => {
      const hasNoContactPhone = !contact?.cellPhone && !contact?.workPhone && !contact?.homePhone;

      if (hasNoContactPhone) {
        Notistack.generalError(ContactErrorMessage.NO_PHONE_ERROR_MESSAGE);
        return;
      }

      const formStatus = JSON.parse(JSON.stringify(formikRef.current?.status ?? ''));
      const isPulling = formStatus === PULL_STATUS;
      const getData = getAssetRequestFormModel(data, contactIdParam, lienHolders);

      const postData = {
        ...getData,
        isAssetOwner: setAssetOwnerValue(data?.isAssetOwner),
        year: data?.year ? data?.year : null,
        assetBrand: {
          brandedStateList: data?.isBrandedTitle ? data.assetBrand?.brandedStateList : [],
          brandName: data?.isBrandedTitle ? data.assetBrand?.brandName : null,
          brandDescription: data?.isBrandedTitle ? data.assetBrand?.brandDescription : null,
        },
        assetValidations: {
          isLiftKit: data?.assetValidations?.isLiftKit ? (data?.assetValidations.isLiftKit === 'Yes' ? true : false) : null,
          isSixInchLiftKit: data?.assetValidations?.isSixInchLiftKit ? (data?.assetValidations.isSixInchLiftKit === 'Yes' ? true : false) : null,
          isHEV: data?.assetValidations?.isHEV ? (data?.assetValidations.isHEV === 'Yes' ? true : false) : null,
          doesOversizeUndersizeTire: data?.assetValidations?.doesOversizeUndersizeTire ? (data?.assetValidations.doesOversizeUndersizeTire === 'Yes' ? true : false) : null,
        },
        coContactId: data?.coContactId ? data?.coContactId : null
      } as IFormAsset;

      if (draftData) {
        dispatch(removeAssetDraftData({ contactId: contactIdParam }));
      }

      if (data.id) {
        const asset = await requestUpdateAsset(postData);

        if (postData.assetLienHolder && !postData.assetLienHolder?.lienHolderId) {
          postData.assetLienHolder.lienHolderId = asset?.assetLienHolder?.lienHolderId ?? null;
        }
      } else {
        const asset: IAsset = await requestCreateAsset(postData);
        postData.id = asset.id;
      }
            
      onSave();
      await reloadAssets();
      await refreshLienHolders();
      await reloadHeaderData();

      if (postData?.mileage !== initialValues?.mileage) {
        dealId && dispatch(setIsDealStructureChanged({data: { isDealStructureChanged: true, assetId: assetIdParam }, dealId}));
      }

      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      formikRef.current?.resetForm({ values: { ...postData,
          isAssetOwner: getAssetOwnerValue(postData?.isAssetOwner),
          assetValidations: {
          isLiftKit: isBoolean(postData?.assetValidations?.isLiftKit) ? (postData?.assetValidations.isLiftKit ? 'Yes' : 'No') : null,
          isSixInchLiftKit: isBoolean(postData?.assetValidations?.isSixInchLiftKit)  ? (postData?.assetValidations.isSixInchLiftKit ? 'Yes' : 'No') : null,
          isHEV: isBoolean(postData?.assetValidations?.isHEV) ? (postData?.assetValidations.isHEV ? 'Yes' : 'No') : null,
          doesOversizeUndersizeTire: isBoolean(postData?.assetValidations?.doesOversizeUndersizeTire) ? (postData?.assetValidations.doesOversizeUndersizeTire ? 'Yes': 'No') : null,
        }
        , isVinLookupSuccess: false } });

      handleAssetChange(postData.id);

      if (isPulling) {
        setAutoPullValuationAfterSave(true);
      }
    },
    [lienHolders, contactIdParam, draftData, assetIdParam, dealId]
  );

  const handleAutoPullValuationComplete = useCallback((isSuccess: boolean) => {
    setAutoPullValuation(false);
    setAutoPullValuationAfterSave(false);

    // save after JD Power and KBB valuations are auto-pulled
    if (isSuccess) {
      if (formikRef.current?.dirty) {
        formikRef.current?.handleSubmit();
      }
    }
  }, []);

  const handleAssetChange = (changedAssetId: string) => {
    setAssetIdParam(changedAssetId);
  };

  const handleCoApplicantChange =  useCallback((coApplicantId: string) => {  
    formikRef.current?.setFieldValue('coContactId',coApplicantId);
  }, []);

  const saveDraftData = useCallback(() => {
    dispatch(
      setAssetDraftData({
        contactId: contactIdParam,
        data: {
          ...formikRef.current?.values,
          id: assetIdParam || ''
        } as IAsset
      }));
    },
    [contactIdParam, assetIdParam]
  );

  return (
    <ContactManagerLayout isLoading={isLoading}>
      {initialValues && (
        <Formik
          key={assetIdParam}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={assetValidationSchema}
          innerRef={formikRef}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <AssetLayout>
              <FormWithPrompt onSaveDraftData={saveDraftData}>
                <AssetsFilter>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sm={12}
                  >
                    <Grid
                      container
                      sm={9}
                      flexWrap="nowrap"
                    >
                      <Grid
                        item
                        sm={4}
                      >
                        <SelectAutocomplete
                          isFitContentWidth
                          removeExtraPadding
                          canBeClean={false}
                          value={assetIdParam}
                          options={getOptions(assetOptions, 'id', 'display')}
                          onChange={handleAssetChange}
                        />
                      </Grid>

                      <Grid item>
                        <Button
                          size="large"
                          variant="text"
                          color="info"
                          type="button"
                          style={{ marginLeft: 10 }}
                          onClick={() => setAssetIdParam('')}
                        >
                          Add Asset
                        </Button>
                      </Grid>

                      <Grid
                        container
                        sm={3}
                        style={{ marginLeft: 50 }}
                      >
                        <Grid
                          item
                          xs
                          style={{ paddingTop: 8 }}
                        >
                          <SelectAutocomplete
                            fullWidth
                            name='coContactId'
                            label="Co-Borrower on new loan"
                            placeholder='None'
                            value={formikRef.current?.values?.coContactId}
                            options={coApplicantOptions}
                            onChange={handleCoApplicantChange}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        sm={3}
                        style={{ marginLeft: 50, display: 'var(--grid-element-visible)' }}
                      >
                        <Grid
                          item
                          xs
                          style={{ paddingTop: 0}}
                        >         
                          <AssetOwnerForm />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      sm
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Grid
                        item
                        sm={4}
                      >
                        <SaveButton
                          variant="contained"
                          width="100%"
                          top="5px"
                          displayMessage="Unsaved Changes"
                          color="info"
                          disabled={!props.dirty}
                          type="submit"
                          label="Save Asset"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </AssetsFilter>
                <Grid
                  container
                  sm={3}
                  style={{ 
                    display: 'var(--grid-element-hidden)',
                    marginLeft: 'var(--grid-element-margin-left)',
                    maxWidth: 'var(--grid-element-max-width)',
                  }}
                >
                  <Grid
                    item
                    xs
                    style={{ paddingTop: 0 }}
                  >         
                    <AssetOwnerForm />
                  </Grid>
                </Grid>
                <UnableToSaveModal />

                <Grid
                  container
                  spacing={2}
                >
                  <CustomGridItem>
                    <Item>
                      <AssetForm
                        pickedDealId={pickedDeal?.dealId}
                        handleSubmit={handleSubmit}
                      />
                    </Item>
                  </CustomGridItem>

                  <CustomGridItem>
                    <Item>
                      <LienForm lienHolders={lienHolders} />
                    </Item>
                  </CustomGridItem>

                  <CustomGridItem>
                    <Item>
                      <ValuationForm
                        zip={zip}
                        state={state}
                        pickedDealId={pickedDeal?.dealId}
                        autoPullValuation={autoPullValuation}
                        autoPullValuationComplete={handleAutoPullValuationComplete}
                      />
                    </Item>
                    <Item>
                      <ValidationForm />
                    </Item>
                  </CustomGridItem>

                  <CustomGridItem>
                    <Item>
                      <RegistrationForm pickedDeal={pickedDeal} />
                    </Item>

                    <Item>
                      <TitleForm />
                    </Item>

                    <Item>
                      <InsuranceForm />
                    </Item>
                  </CustomGridItem>
                </Grid>
              </FormWithPrompt>
            </AssetLayout>
          )}
        </Formik>
      )}
    </ContactManagerLayout>
  );
};

